import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  CircularProgress,
  FormControl,
  FormLabel,
} from "@mui/material";
import { useGetDatasetQuestionVariablesByElementQuery } from "@/store/api/dataset-api";
import HPTag from "../common/Tag";

const DEFAULT_WEIGHT_VALUE = "__DEFAULT_WEIGHT__";

interface WeightConfiguration {
  configurationId: string;
  surveyId: string;
  surveyName: string;
  datasetElementQuestionId: string;
  questionLabel: string;
}

interface PerLineWeightRowProps {
  lineId: string; // series.id from chart data
  lineLabel: string; // series.name for display
  datasetId: string;
  weightConfigurations: WeightConfiguration[];
  selectedWeight?: {
    surveyId: string;
    configurationId: string;
    datasetElementQuestionId: string;
    datasetElementVariableId: string;
    weightVariableLabel: string;
  };
  onWeightChange: (
    surveyId: string,
    configurationId: string,
    datasetElementQuestionId: string,
    datasetElementVariableId: string,
    weightVariableLabel: string,
  ) => void;
  onClearWeight?: (lineId: string) => void;
  isHiddenLine?: boolean;
  disabled?: boolean;
}

const PerLineWeightRow: React.FC<PerLineWeightRowProps> = ({
  lineId,
  lineLabel,
  datasetId,
  weightConfigurations,
  selectedWeight,
  onWeightChange,
  onClearWeight,
  isHiddenLine = false,
  disabled = false,
}) => {
  const [selectedConfigId, setSelectedConfigId] = useState<string>(
    selectedWeight?.configurationId || "",
  );
  const [selectedVariableId, setSelectedVariableId] = useState<string>(
    selectedWeight?.datasetElementVariableId || "",
  );

  // Find the selected survey config to get question ID
  const selectedConfig = weightConfigurations.find(
    (config) => config.configurationId === selectedConfigId,
  );

  // Each row independently fetches variables for its own selected question
  const { data: weightVariablesData, isLoading: isLoadingWeightVariables } =
    useGetDatasetQuestionVariablesByElementQuery(
      {
        id: datasetId,
        dataset_element_id: selectedConfig?.datasetElementQuestionId || "",
      },
      {
        skip: !datasetId || !selectedConfig?.datasetElementQuestionId,
        refetchOnMountOrArgChange: false,
        refetchOnFocus: false,
        refetchOnReconnect: false,
      },
    );

  const weightVariables = useMemo(() => {
    return weightVariablesData?.variables || [];
  }, [weightVariablesData]);

  // Create a mapping of variable label to variable ID
  const variableMapping = useMemo(() => {
    const mapping: Record<string, string> = {};
    if (weightVariables) {
      weightVariables.forEach((variable) => {
        mapping[variable.label] = variable.datasetElementId;
      });
    }
    return mapping;
  }, [weightVariables]);

  // Sync with external changes (including when weight is cleared)
  useEffect(() => {
    if (selectedWeight) {
      setSelectedConfigId(selectedWeight.configurationId);
      setSelectedVariableId(selectedWeight.datasetElementVariableId);
    } else {
      // Weight was cleared externally (e.g., "Set all to Default Weight" button)
      setSelectedConfigId("");
      setSelectedVariableId("");
    }
  }, [selectedWeight]);

  const handleSurveyChange = (configId: string) => {
    if (configId === DEFAULT_WEIGHT_VALUE) {
      // Clear weight - use dataset default
      setSelectedConfigId("");
      setSelectedVariableId("");
      if (onClearWeight) {
        onClearWeight(lineId);
      }
    } else {
      setSelectedConfigId(configId);
      setSelectedVariableId(""); // Reset variable selection
    }
  };

  const handleVariableChange = (variableLabel: string) => {
    // variableLabel is the display name, we need to get the ID from mapping
    const variableId = variableMapping[variableLabel];

    console.log("🔵 PerLineWeightRow - handleVariableChange:", {
      lineId,
      lineLabel,
      variableLabel,
      variableId,
      selectedConfigId,
      selectedConfig,
    });

    if (variableId && selectedConfigId && selectedConfig) {
      setSelectedVariableId(variableId);
      onWeightChange(
        selectedConfig.surveyId,
        selectedConfigId,
        selectedConfig.datasetElementQuestionId,
        variableId,
        variableLabel,
      );
    }
  };

  // Find the selected variable label for display
  const getSelectedVariableLabel = () => {
    if (!selectedVariableId) return "";
    // Find the label from variables array that maps to this ID
    for (const [label, id] of Object.entries(variableMapping)) {
      if (id === selectedVariableId) {
        return label;
      }
    }
    return "";
  };

  return (
    <Box>
      {/* Line Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            fontSize: "14px",
            color: "#262626",
          }}
        >
          {lineLabel}
        </Typography>
        {isHiddenLine && <HPTag ariaLabel="Hidden">Hidden</HPTag>}
      </Box>

      {/* Question Selection */}
      <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={disabled}>
        <FormLabel
          sx={{
            mb: 0.5,
            fontSize: "12px",
            color: "#666",
            fontWeight: 400,
          }}
        >
          Question
        </FormLabel>
        <Select
          value={selectedConfigId || DEFAULT_WEIGHT_VALUE}
          onChange={(e) => handleSurveyChange(e.target.value)}
          displayEmpty
          sx={{
            backgroundColor: "#fff",
            "& .MuiSelect-select": {
              py: 1,
              fontSize: "14px",
            },
          }}
        >
          <MenuItem value={DEFAULT_WEIGHT_VALUE}>
            <em>Default Weight</em>
          </MenuItem>
          {weightConfigurations.map((config) => (
            <MenuItem
              key={config.configurationId}
              value={config.configurationId}
            >
              {config.questionLabel}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Variable Selection */}
      <FormControl
        fullWidth
        size="small"
        disabled={disabled || !selectedConfigId}
      >
        <FormLabel
          sx={{
            mb: 0.5,
            fontSize: "12px",
            color: "#666",
            fontWeight: 400,
          }}
        >
          Variable
        </FormLabel>
        {isLoadingWeightVariables ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 2,
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              backgroundColor: "#fff",
            }}
          >
            <CircularProgress size={20} />
          </Box>
        ) : (
          <Select
            value={getSelectedVariableLabel()}
            onChange={(e) => handleVariableChange(e.target.value)}
            displayEmpty
            disabled={!selectedConfigId || weightVariables.length === 0}
            sx={{
              backgroundColor: "#fff",
              "& .MuiSelect-select": {
                py: 1,
                fontSize: "14px",
              },
            }}
          >
            <MenuItem value="">
              <em>Select Variable</em>
            </MenuItem>
            {weightVariables.map((variable) => (
              <MenuItem key={variable.datasetElementId} value={variable.label}>
                {variable.label}
              </MenuItem>
            ))}
          </Select>
        )}
      </FormControl>
    </Box>
  );
};

export default PerLineWeightRow;
